/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-statements */
import { ComponentCategories } from '../component-categories/component-categories.class';
import { ComponentCategory } from '../component-categories/component-category.class';
import { componentData } from './component.data';
import { ComponentInstanceType } from '../shared-enums.ts';
import { ComponentOptions } from '../types';
import { ComponentVendor } from '../component-vendors/component-vendor.class';
import { ComponentVendors } from '../component-vendors/component-vendors.class';
import { ControllerNodeBase } from '../../controller-classes';
import { Publish } from '../../decorators';
import { resetCfn } from './functions';
import { Rules } from '../../blueprint-classes';
import { Size } from '../../types';
import { Vertex3 } from '../../spatial-classes';

import { ComponentData, ComponentDataIn, ComponentDimensionOptions, ComponentDynamicSvg, DefaultPlacement, ImageCorrection, SkuOverheadMode } from './types';
import { getSvgData } from '../data/component-svg';

export class Component extends ControllerNodeBase<ComponentDataIn, ComponentInstanceType.Component> {
	#categories: ComponentCategories;
	#vendors: ComponentVendors;

	#id: string;
	#application: string;
	#category: ComponentCategory;
	#placement: string;
	#vendor: ComponentVendor;
	#readonly: boolean;
	#savedVaues: ComponentData;

	#categoryId: string;
	#cutout: Size;
	#description: string;
	#blueprintDescription: string;
	#distanceAboveCountertop: number | null;
	#fuel: boolean;
	#render: boolean;
	#size: Size;
	#sku: string;
	#label: string;
	#customLabel: string;
	#vendorId: string;
	#svgFuncName: string;
	readonly svg: ComponentDynamicSvg;

	public applianceModule: string;
	public distanceFrontOfFace: number | null;
	public distanceBackOfFace: number | null;
	public distanceBelowCountertop: number | null;
	public defaultPlacement: DefaultPlacement | null;
	#imageCorrection: ImageCorrection;
	#modelOffset: Vertex3;
	#modelRotation: Vertex3;

	public dimensions: ComponentDimensionOptions;
	public displayName: string;
	public showLabel: boolean;
	public showImage: boolean;
	public showInOverhead: boolean;
	public showInSkuOverhead: boolean;
	public showFuelSku: boolean;
	public fuelFamily: string | null;
	public fuelType: string | null;
	public skuOverheadMode: SkuOverheadMode;
	public skuColorPreset: string;
	public rules: Rules;
	public validationRules: Array<string>;
	public active: boolean;
	public msrp: number;
	public hsProductId: string | null;
	public modelFile: string | null;
	public imageFile: string | null;
	public sleeveCompatible: boolean;
	public sleeveDepth: number;
	#aiContext: string | null;
	public ruleManaged: boolean;
	public fullHeight: boolean;
	#placeholderModelSku: string | null;

	constructor (options: ComponentOptions<ComponentDataIn>) {
		super(ComponentInstanceType.Component, options);

		this.#categories = options.componentCategories ?? new ComponentCategories({ ...options, dataIn: null });
		this.#vendors = options.componentVendors ?? new ComponentVendors({ ...options, dataIn: null });
		const data = componentData(options);

		// initialize as false to allow seters
		this.#readonly = false;

		// load component data
		this.#id = data.id;
		this.#sku = data.sku;
		this.#label = data.label ?? data.sku;
		this.displayName = data.displayName ?? null;
		this.#customLabel = data.customLabel;
		this.#description = data.description;
		this.#blueprintDescription = data.blueprintDescription ?? '';
		this.#placement = data.placement;
		this.#application = data.application;
		this.applianceModule = data.applianceModule;
		this.#cutout = data.cutout;
		this.#size = data.size;
		this.#distanceAboveCountertop = data.distanceAboveCountertop;
		this.distanceFrontOfFace = data.distanceFrontOfFace;
		this.distanceBackOfFace = data.distanceBackOfFace;
		this.distanceBelowCountertop = data.distanceBelowCountertop;
		this.defaultPlacement = data.defaultPlacement;
		this.#imageCorrection = data.imageCorrection;
		this.dimensions = data.dimensions;
		this.showLabel = data.showLabel;
		this.showImage = data.showImage;
		this.showInOverhead = data.showInOverhead ?? true;
		this.showInSkuOverhead = data.showInSkuOverhead ?? true;
		this.showFuelSku = data.showFuelSku ?? true;
		this.fuelFamily = data.fuelFamily ?? null;
		this.fuelType = data.fuelType ?? null;
		this.skuOverheadMode = data.skuOverheadMode ?? 'sku';
		this.skuColorPreset = data.skuColorPreset ?? 'White';
		this.rules = data.rules;
		this.active = data.active;
		this.#fuel = data.fuel;
		this.#render = data.render;
		this.#svgFuncName = (data.svg as unknown as string) ?? null;
		this.svg = getSvgData(this.#svgFuncName);
		this.msrp = data.msrp;
		this.hsProductId = data.hsProductId ?? null;
		this.modelFile = data.modelFile ?? null;
		this.imageFile = data.imageFile ?? null;

		// load remaining data using setters
		this.categoryId = data.categoryId;
		this.vendorId = data.vendorId;

		// set readonly state
		this.readonly = options?.readonly ?? true;
		this.#modelOffset = new Vertex3({ dataIn: data.modelOffset });
		this.#modelRotation = new Vertex3({ dataIn: data.modelRotation });

		this.sleeveCompatible = data.sleeveCompatible;
		this.sleeveDepth = data.sleeveDepth;
		this.#aiContext = data.aiContext ?? null;
		this.ruleManaged = data.ruleManaged ?? true;
		this.fullHeight = data.fullHeight ?? false;
		this.#placeholderModelSku = data.placeholderModelSku ?? null;

		this.setInitialized();
	}

	get id (): string { return this.#id }

	@Publish('component_application', 'updated')
	set application (application: string) {
		if (!this.#readonly) this.#application = application;
	}
	get application (): string { return this.#application }

	@Publish('component_cutout', 'updated')
	set cutout (cutout: Partial<Size>) {
		if (!this.#readonly) this.#cutout = { ...this.#cutout, ...cutout };
	}
	get cutout (): Size { return this.#cutout }

	@Publish('component_distance_above_countertop', 'updated')
	set distanceAboveCountertop (distance: number | null) {
		if (!this.#readonly) this.#distanceAboveCountertop = distance;
	}
	get distanceAboveCountertop (): number { return this.#distanceAboveCountertop }

	@Publish('component_placement', 'updated')
	set placement (placement: string) {
		if (!this.#readonly) this.#placement = placement;
	}
	get placement (): string { return this.#placement }

	@Publish('component_category', 'updated')
	set category (category: ComponentCategory) {
		if (!this.#readonly) {
			this.#category = category;
			this.#categoryId = category.id;
		}
	}
	get category (): ComponentCategory { return this.#category }

	@Publish('component_category_id', 'updated')
	set categoryId (id: string) {
		if (!this.#readonly) {
			this.#category = this.#categories.findById(id) ?? null;
			this.#categoryId = this.#category ? id : null;
		}
	}
	get categoryId (): string { return this.#categoryId }

	@Publish('component_description', 'updated')
	set description (description: string) {
		if (!this.#readonly) this.#description = description;
	}
	get description (): string { return this.#description }

	get blueprintDescription (): string { return this.#blueprintDescription }

	@Publish('component_fuel', 'updated')
	set fuel (fuel: boolean) {
		if (!this.#readonly) this.#fuel = fuel;
	}
	get fuel (): boolean { return this.#fuel }

	@Publish('component_render', 'updated')
	set render (render: boolean) {
		if (!this.#readonly) this.#render = render;
	}
	get render (): boolean { return this.#render }

	@Publish('component_size', 'updated')
	set size (size: Partial<Size>) {
		if (!this.#readonly) this.#size = { ...this.#size, ...size };
	}
	get size (): Size { return this.#size }

	@Publish('component_sku', 'updated')
	set sku (sku: string) {
		if (!this.#readonly) this.#sku = sku;
	}
	get sku (): string { return this.#sku; }

	@Publish('component_sku', 'updated')
	get label (): string { return this.#label; }
	set label (label: string) {
		if (!this.#readonly) this.#label = label;
	}

	@Publish('component_custom_label', 'updated')
	set customLabel (label: string) {
		if (!this.#readonly) this.#customLabel = label;
	}
	get customLabel (): string { return this.#customLabel }

	@Publish('component_vendor', 'updated')
	set vendor (vendor: ComponentVendor) {
		if (!this.#readonly) {
			this.#vendor = vendor;
			this.#vendorId = vendor.id;
		}
	}
	get vendor (): ComponentVendor { return this.#vendor }

	@Publish('component_vendor_id', 'updated')
	set vendorId (id: string) {
		if (!this.#readonly) {
			this.#vendor = this.#vendors.findById(id) ?? null;
			this.#vendorId = this.#vendor ? id : null;
		}
	}
	get vendorId (): string { return this.#vendorId }

	@Publish('component_image_correction', 'updated')
	set imageCorrection (value: ImageCorrection) {
		this.#imageCorrection = value ? { ...value } : value;
	}
	get imageCorrection (): ImageCorrection { return this.#imageCorrection }

	@Publish('component_model_offset', 'updated')
	set modelOffset (value: Vertex3) {
		this.#modelOffset = value;
	}
	get modelOffset (): Vertex3 { return this.#modelOffset }

	@Publish('component_model_rotation', 'updated')
	set modelRotation (value: Vertex3) {
		this.#modelRotation = value;
	}
	get modelRotation (): Vertex3 { return this.#modelRotation }

	@Publish('component_readonly', 'updated')
	set readonly (ro: boolean) {
		if (!this.#readonly && ro) {
			this.reset();
			this.#savedVaues = null;
			this.#readonly = true;
		} else if (this.#readonly && !ro) {
			this.#savedVaues = this.serialize();
			this.#readonly = false;
		}
	}
	get readonly (): boolean { return this.#readonly }

	get aiContext (): string | null { return this.#aiContext ?? null }

	@Publish('component', 'placeholder_model_sku_updated')
	set placeholderModelSku (sku: string | null) {
		if (this.#readonly) return;
		// reject self-reference: a component cannot use itself as a placeholder
		if (sku === this.#sku) return;
		this.#placeholderModelSku = sku ?? null;
	}
	get placeholderModelSku (): string | null { return this.#placeholderModelSku }

	public reset (): void { resetCfn(this, this.#savedVaues) }

	public serialize ():ComponentData {
		return {
			id: this.#id,
			categoryId: this.#categoryId,
			vendorId: this.#vendorId,
			sku: this.sku,
			label: this.label,
			displayName: this.displayName,
			customLabel: this.#customLabel,
			description: this.description,
			blueprintDescription: this.#blueprintDescription,
			placement: this.placement,
			application: this.application,
			applianceModule: this.applianceModule,
			cutout: this.cutout,
			size: this.size,
			distanceAboveCountertop: this.distanceAboveCountertop,
			distanceFrontOfFace: this.distanceFrontOfFace,
			distanceBackOfFace: this.distanceBackOfFace,
			distanceBelowCountertop: this.distanceBelowCountertop,
			defaultPlacement: this.defaultPlacement,
			imageCorrection: this.imageCorrection,
			dimensions: this.dimensions,
			showLabel: this.showLabel,
			showImage: this.showImage,
			showInOverhead: this.showInOverhead,
			showInSkuOverhead: this.showInSkuOverhead,
			showFuelSku: this.showFuelSku,
			fuelFamily: this.fuelFamily,
			fuelType: this.fuelType,
			skuOverheadMode: this.skuOverheadMode,
			skuColorPreset: this.skuColorPreset,
			rules: this.rules,
			active: this.active,
			fuel: this.fuel,
			render: this.#render,
			svg: this.#svgFuncName,
			msrp: this.msrp,
			hsProductId: this.hsProductId,
			modelFile: this.modelFile,
			imageFile: this.imageFile,
			modelOffset: this.modelOffset.serialize(),
			modelRotation: this.modelRotation.serialize(),
			sleeveCompatible: this.sleeveCompatible,
			sleeveDepth: this.sleeveDepth,
			ruleManaged: this.ruleManaged,
			fullHeight: this.fullHeight,
			placeholderModelSku: this.#placeholderModelSku,
			...(this.#aiContext != null ? { aiContext: this.#aiContext } : {})
		};
	}
}
